/// <reference path='common.ts' />
/// <reference path='fetch.ts' />

/**
 * 汎用的なデータとエレメントを連携させる機能です。data-bind属性にデータをJSON形式で設定します。
 * 属性およびコンテンツにおいて{{}}で囲んだ文字列をスクリプトとして実行し戻り値と置換します。
 * スクリプトではdata-bindで指定されたパラメータを変数として使用できます。
 * 属性に指定された場合で戻り値がfalseの場合は属性自身を除去、trueの場合は値なしとします。
 *
 * 属性
 * data-bind: 参照するデータ
 * data-bind-extend: 先祖エレメントのdata-bind属性と結合する
 * data-bind-default: 参照データのデフォルト値（data-bindと同じエレメントの場合に結合される）
 * data-bind-fetch: URLからフェッチした内容をdata-bindに割り当てる
 * data-bind-fetch-arg: フェッチした内容を設定する変数名（省略時は展開する）
 * data-bind-fetch-merge: フェッチ結果を元のdeta-bindとマージする
 * data-bind-fetch-click: フェッチが完了した際に当該エレメントに対してクリックイベントを発生させる
 * data-bind-fetch-redirect: フェッチに成功した際に遷移するURL
 * data-bind-method: フェッチする際のメソッド（省略時はGET）
 * data-bind-enctype: フェッチする際に送信するContent-Type（GETメソッドの場合はapplication/x-www-form-urlencoded、それ以外場合はapplication/jsonがデフォルト）
 * data-bind-form: フェッチする際に付与するパラメータが格納されているフォームのセレクタ（値が空の場合は親フォーム）フェッチしない場合はパラメータが値として利用される
 * data-bind-cache-use: キャッシュした内容をフェッチの代わりに使用する場合（値をキーとし、設定していない場合はフェッチURLをキーとする）
 * data-bind-cache-set: フェッチした内容をキャッシュする（値をキーとし、設定していない場合はフェッチURLをキーとする）
 * data-bind-cache-clear: クリックした際もしくはフェッチに成功した際にキャッシュをクリアする（値をキーとし、設定していない場合はフェッチURLをキーとする）
 * data-bind-each: 属性値に指定されたリストの数だけ子エレメントをコピーし、それぞれのエレメントのdata-bind属性に値を設定する（indexパラメータにインデックスを付ける）
 * data-bind-each-before: 繰り返しの対象とせずに繰り返しの前に表示されます
 * data-bind-each-after: 繰り返しの対象とせずに繰り返しの後に表示されます
 * data-bind-each-arg: 繰り返しエレメントのdata-bind属性に設定する値の変数名（省略時は展開する）
 * data-bind-if: 表示判定（戻り値がfalseかnullなら非表示、その他なら元の表示状態とする）
 * data-bind-if-false: data-bind-ifで非表示の場合（変更しないで下さい）
 * data-bind-invalid: 当該エレメント以下のエレメントのbind処理を無効にする
 * data-bind-transfer: 属性名を付け替える（変換元属性名と変換先属性名をスペースで区切る、変換元属性値にスクリプトが含まれている場合はスキップする）
 * data-bind-fetch-current: 最後に処理したdata-bind-fetch属性の値（変更しないで下さい）
 * data-bind-copy: フェッチした内容をコピーして設定するエレメントのセレクタ
 *
 * イベント
 * fetch: 通信が完了した場合（detailはrequestとresponseのPromise）
 * fetch-error: 通信中にエラーが発生した場合（detailはrequestとエラー内容）
 * change-bind: data-bind属性の値が変更された場合
 */
class HaoriBind {
  /** イベントの発生を抑制するエレメントのセット */
  private static stopEventElements = new WeakSet<Element>();

  /** エレメントに対する変更前のコンテンツ */
  private static srcContents = new WeakMap<ChildNode, string>();

  /** エレメントに対するアトリビュート名に対する変更前のアトリビュート値 */
  private static srcAttributes = new WeakMap<Element, Map<string, string>>();

  /** エレメントに対するアトリビュート名に対する取り除かれたアトリビュートのセット */
  private static removeAttributes = new WeakMap<Element, Set<string>>();

  /** ifエレメントに対する中身 */
  private static srcIfElements = new WeakMap<Element, Array<Node>>();

  /** eachエレメントに対するバインドオブジェクトの文字列 */
  private static eachBindStrings = new WeakMap<Element, string>();

  /** eachエレメントに対するスクリプト */
  private static eachScripts = new WeakMap<Element, string>();

  /**
   * 文字列を変更中のノードに対する変更回数のマップ。
   * 変更通知はまとめて届くため、同じノードを続けて変更しても自身の変更と判定できるよう回数で管理します。
   */
  private static contentChangeNodes = new WeakMap<Node, number>();

  /** アトリビュートを変更中のエレメントに対するアトリビュート名ごとの変更回数のマップ */
  private static attributeChangeNodes = new WeakMap<Element, Map<string, number>>();

  /**
   * データとして挿入したノードのセット（Haoriやアプリケーションがデータとして挿入したもの）。
   * テンプレートとして登録しないため、値に{{}}が含まれていてもスクリプトとして実行しません。
   */
  private static dataNodes = new WeakSet<Node>();

  /**
   * Haori以外がテキストを変更したノードのセット。
   * データとして扱いますが、Haori.registerTemplateで明示的に登録した場合はテンプレートとして登録します。
   */
  private static externalNodes = new WeakSet<Node>();

  /** エレメントに対するHaori以外が変更したアトリビュート名のセット（データとして扱う） */
  private static dataAttributes = new WeakMap<Element, Set<string>>();

  /**
   * エレメントに対するHaoriがデータを書き込んだアトリビュート名のセット。
   * テンプレートとして登録しないため、Haori.registerTemplateで登録した場合も値の{{}}をスクリプトとして実行しません。
   */
  private static writtenDataAttributes = new WeakMap<Element, Set<string>>();

  /** HTMLの解析中に読み込まれた場合はtrue（解析後に読み込まれた場合は読み込み時点で全体を登録できる） */
  private static loadedWhileParsing = document.readyState == 'loading';

  /** エレメントに対する繰り返し前の初期HTML */
  private static srcBeforeEachs = new WeakMap<Element, string>();

  /**
   * エレメントに対する繰り返しの前後に表示するノードのリスト。
   * HTMLの文字列から作り直すとデータとして記録した印が失われるため、複製したノードで保持します。
   */
  private static srcBeforeEachNodes = new WeakMap<Element, Array<Node>>();

  /** エレメントに対するテンプレートエレメントのリスト */
  private static srcEachs = new WeakMap<Element, Array<Element>>();

  /** アトリビュート変更オブザーバ */
  static attributeObserver = new MutationObserver((mutations) => {
    this.attributeChanged(mutations);
  });

  /** テキスト変更オブザーバ */
  static contentObserver = new MutationObserver((mutations) => {
    this.contentChanged(mutations);
  });

  /** エレメント変更オブザーバ */
  static childObserver = new MutationObserver((mutations) => {
    this.childAdded(mutations);
  });

  /**
   * テキストが変更された場合の処理を行います。
   * Haori以外による変更はデータとして扱い、スクリプトとして実行しません。
   *
   * @param mutations 変更情報のリスト
   */
  private static contentChanged(mutations: MutationRecord[]) {
    mutations.forEach((mutation) => {
      if (this.consumeContentChange(mutation.target)) {
        return;
      }
      this.unregisterContent(mutation.target);
    });
  }

  /**
   * エレメントが追加された場合の処理を行います。
   *
   * @param mutations 変更情報のリスト
   */
  private static childAdded(mutations: MutationRecord[]) {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType == Node.ELEMENT_NODE) {
          if (document.readyState == 'loading') {
            // 読み込み中にHTMLの解析で追加されたエレメントはテンプレートとして登録する
            this.registerTemplate(node);
          }
          this.bind(<Element>node);
        }
      });
    });
  }

  /**
   * 通知前の変更をすべて処理します。
   */
  static flushMutations() {
    this.attributeChanged(this.attributeObserver.takeRecords());
    this.contentChanged(this.contentObserver.takeRecords());
    this.childAdded(this.childObserver.takeRecords());
  }

  /**
   * ノード以下をテンプレートとして登録します。
   * 登録したテキストとアトリビュートだけを、{{}}を含む場合にスクリプトとして実行します。
   * 登録していないテキストとアトリビュートはデータとして扱い、そのまま表示します。
   * 登録済みのテキストとアトリビュート（評価済みの値で元のテンプレートを上書きしないため）と、データとして挿入したノードは変更しません。
   *
   * @param node 対象ノード
   * @param external Haori以外が変更したテキストとアトリビュートも登録する場合はtrue
   */
  static registerTemplate(node: Node, external: boolean = false) {
    if (this.dataNodes.has(node)) {
      return;
    }
    if (node.nodeType == Node.TEXT_NODE) {
      if (this.externalNodes.has(node)) {
        if (!external) {
          return;
        }
        this.externalNodes.delete(node);
      }
      if (!this.srcContents.has(<ChildNode>node)) {
        this.srcContents.set(<ChildNode>node, <string>node.textContent);
      }
      return;
    }
    if (node.nodeType != Node.ELEMENT_NODE) {
      return;
    }
    let element = <Element>node;
    let map = this.srcAttributes.get(element);
    if (map == null) {
      map = new Map();
      this.srcAttributes.set(element, map);
    }
    let dataAttributes = this.dataAttributes.get(element);
    let writtenDataAttributes = this.writtenDataAttributes.get(element);
    for (let i = 0; i < element.attributes.length; i++) {
      let attribute = element.attributes[i];
      if (writtenDataAttributes != null && writtenDataAttributes.has(attribute.name)) {
        continue;
      }
      if (dataAttributes != null && dataAttributes.has(attribute.name)) {
        if (!external) {
          continue;
        }
        dataAttributes.delete(attribute.name);
      }
      if (!map.has(attribute.name)) {
        map.set(attribute.name, attribute.value);
      }
    }
    let externalElement = this.externalNodes.has(element);
    if (externalElement && external) {
      this.externalNodes.delete(element);
    }
    if (element.childNodes.length == 1 && !this.srcContents.has(element) && (!externalElement || external)) {
      let child = element.childNodes[0];
      if (!this.dataNodes.has(child) && (!this.externalNodes.has(child) || external)) {
        this.srcContents.set(element, <string>element.textContent);
      }
    }
    element.childNodes.forEach((child) => {
      this.registerTemplate(child, external);
    });
  }

  /**
   * 登録したアトリビュートの元のテンプレートを取得します。
   *
   * @param element 対象エレメント
   * @param attribute アトリビュート名
   * @return 元のテンプレート（登録していない場合はnull）
   */
  static getTemplateAttribute(element: Element, attribute: string): string | null {
    let map = this.srcAttributes.get(element);
    let text = map == null ? undefined : map.get(attribute);
    return text == null ? null : text;
  }

  /**
   * エレメントの中身をテンプレートのHTMLに置き換え、テンプレートとして登録します。
   *
   * @param element 対象エレメント
   * @param html テンプレートのHTML
   */
  static setTemplateHtml(element: Element, html: string) {
    element.innerHTML = html;
    element.childNodes.forEach((child) => {
      this.registerTemplate(child);
    });
  }

  /**
   * Haoriがデータを書き込むアトリビュートとして記録し、値を設定します。
   * 記録したアトリビュートはテンプレートとして登録せず、値の{{}}をスクリプトとして実行しません。
   *
   * @param element 対象エレメント
   * @param attribute アトリビュート名
   * @param value 値
   */
  static setDataAttribute(element: Element, attribute: string, value: string) {
    this.markDataAttribute(element, attribute);
    element.setAttribute(attribute, value);
  }

  /**
   * Haoriがデータを書き込むアトリビュートとして記録します（プロパティ経由でアトリビュートが変わる場合など）。
   *
   * @param element 対象エレメント
   * @param attribute アトリビュート名
   */
  static markDataAttribute(element: Element, attribute: string) {
    let set = this.writtenDataAttributes.get(element);
    if (set == null) {
      set = new Set();
      this.writtenDataAttributes.set(element, set);
    }
    set.add(attribute);
  }

  /**
   * ノード以下をデータとして挿入したものとして記録します。
   * 記録したノードはテンプレートとして登録せず、{{}}を含んでいてもスクリプトとして実行しません。
   *
   * @param node 対象ノード
   */
  static markAsData(node: Node) {
    this.dataNodes.add(node);
    node.childNodes.forEach((child) => {
      this.markAsData(child);
    });
  }

  /**
   * テキストの登録を解除し、以後はデータとして扱います。
   *
   * @param node 対象ノード
   */
  private static unregisterContent(node: Node) {
    this.externalNodes.add(node);
    this.srcContents.delete(<ChildNode>node);
    let parent = node.parentElement;
    if (parent != null) {
      this.externalNodes.add(parent);
      this.srcContents.delete(parent);
    }
  }

  /**
   * アトリビュートの登録を解除し、以後はデータとして扱います。
   *
   * @param element 対象エレメント
   * @param attribute アトリビュート名
   */
  private static unregisterAttribute(element: Element, attribute: string) {
    let set = this.dataAttributes.get(element);
    if (set == null) {
      set = new Set();
      this.dataAttributes.set(element, set);
    }
    set.add(attribute);
    this.srcAttributes.get(element)?.delete(attribute);
    this.removeAttributes.get(element)?.delete(attribute);
  }

  /**
   * 自身によるテキストの変更として記録します。
   *
   * @param node 対象ノード
   */
  private static addContentChange(node: Node) {
    if (!node.isConnected) {
      // 文書に組み込まれていないノードの変更は通知されないため記録しない
      return;
    }
    this.contentChangeNodes.set(node, (this.contentChangeNodes.get(node) || 0) + 1);
  }

  /**
   * 自身によるテキストの変更であれば記録を1回分消費します。
   *
   * @param node 対象ノード
   * @return 自身による変更の場合はtrue
   */
  private static consumeContentChange(node: Node): boolean {
    let count = this.contentChangeNodes.get(node);
    if (count == null) {
      return false;
    }
    if (count <= 1) {
      this.contentChangeNodes.delete(node);
    } else {
      this.contentChangeNodes.set(node, count - 1);
    }
    return true;
  }

  /**
   * 自身によるアトリビュートの変更として記録します。
   *
   * @param element 対象エレメント
   * @param attribute アトリビュート名
   */
  private static addAttributeChange(element: Element, attribute: string) {
    if (!element.isConnected) {
      // 文書に組み込まれていないエレメントの変更は通知されないため記録しない
      return;
    }
    let map = this.attributeChangeNodes.get(element);
    if (map == null) {
      map = new Map();
      this.attributeChangeNodes.set(element, map);
    }
    map.set(attribute, (map.get(attribute) || 0) + 1);
  }

  /**
   * 自身によるアトリビュートの変更であれば記録を1回分消費します。
   *
   * @param element 対象エレメント
   * @param attribute アトリビュート名
   * @return 自身による変更の場合はtrue
   */
  private static consumeAttributeChange(element: Element, attribute: string): boolean {
    let map = this.attributeChangeNodes.get(element);
    let count = map?.get(attribute);
    if (map == null || count == null) {
      return false;
    }
    if (count <= 1) {
      map.delete(attribute);
      if (map.size == 0) {
        this.attributeChangeNodes.delete(element);
      }
    } else {
      map.set(attribute, count - 1);
    }
    return true;
  }

  /**
   * ノードを複製します。Node.cloneNodeではなくこちらを使用してください。
   *
   * @param node 対象エレメント
   * @return 複製したノード
   */
  static clone(node: Node): Node {
    let cloned = node.cloneNode();
    if (node.nodeType == Node.ELEMENT_NODE) {
      let element = <Element>node;
      let cloneElement = <Element>cloned;
      if (this.srcAttributes.has(element)) {
        let srcMap = this.srcAttributes.get(element);
        this.srcAttributes.set(cloneElement, new Map(srcMap));
      }
      if (this.removeAttributes.has(element)) {
        let srcSet = this.removeAttributes.get(element);
        this.removeAttributes.set(cloneElement, new Set(srcSet));
      }
      if (this.srcIfElements.has(element)) {
        let cloneNodes = new Array<Node>();
        this.srcIfElements.get(element)?.forEach((node) => {
          cloneNodes.push(HaoriBind.clone(node));
        });
        this.srcIfElements.set(cloneElement, cloneNodes);
      }
      if (this.eachBindStrings.has(element)) {
        let src = this.eachBindStrings.get(element);
        this.eachBindStrings.set(cloneElement, src!);
      }
      if (this.eachScripts.has(element)) {
        let src = this.eachScripts.get(element);
        this.eachScripts.set(cloneElement, src!);
      }
      if (this.srcEachs.has(element)) {
        let src = this.srcEachs.get(element);
        this.srcEachs.set(cloneElement, src!);
      }
      if (this.srcBeforeEachs.has(element)) {
        let src = this.srcBeforeEachs.get(element);
        this.srcBeforeEachs.set(cloneElement, src!);
      }
      if (this.srcBeforeEachNodes.has(element)) {
        let src = this.srcBeforeEachNodes.get(element);
        this.srcBeforeEachNodes.set(cloneElement, src!);
      }
    }
    if (this.dataNodes.has(node)) {
      this.dataNodes.add(cloned);
    }
    if (this.externalNodes.has(node)) {
      this.externalNodes.add(cloned);
    }
    if (node.nodeType == Node.ELEMENT_NODE && this.dataAttributes.has(<Element>node)) {
      this.dataAttributes.set(<Element>cloned, new Set(this.dataAttributes.get(<Element>node)));
    }
    if (node.nodeType == Node.ELEMENT_NODE && this.writtenDataAttributes.has(<Element>node)) {
      this.writtenDataAttributes.set(<Element>cloned, new Set(this.writtenDataAttributes.get(<Element>node)));
    }
    if (this.srcContents.has(<ChildNode>node)) {
      this.srcContents.set(<ChildNode>cloned, this.srcContents.get(<ChildNode>node)!);
    }
    // テンプレートの登録は子ノードの複製ごとに引き継ぐ（複製前の子ノードの位置には対応する複製がまだ無いため）
    node.childNodes.forEach((child) => {
      cloned.appendChild(HaoriBind.clone(child));
    });
    return cloned;
  }

  /**
   * アトリビュートが変更された場合の処理を行います。
   *
   * @param mutations 変更情報のリスト
   */
  private static attributeChanged(mutations: MutationRecord[]) {
    mutations.forEach((mutation) => {
      let element = <HTMLElement>mutation.target;
      if (mutation.attributeName && this.consumeAttributeChange(element, mutation.attributeName)) {
        return;
      }
      if (mutation.attributeName != null && element.getAttribute(mutation.attributeName) == mutation.oldValue) {
        // 変更なし
        return;
      }
      if (element.closest('html') == null) {
        // DOMに組み込まれていないエレメントはスキップする
        return;
      }
      let transfers: Array<string> | null = null;
      if (element.hasAttribute('data-bind-transfer')) {
        transfers = (<string>element.getAttribute('data-bind-transfer')).split(' ');
        if (transfers.length != 2) {
          transfers = null;
          console.error('data-bind-transfer属性の値は（変換元属性名）" "（変換先属性名）で記述してください');
        }
      }
      switch (mutation.attributeName) {
        case 'data-bind':
        case 'data-bind-default':
          this.bind(element);
          if (this.stopEventElements.has(element)) {
            this.stopEventElements.delete(element);
          } else {
            if (element.hasAttribute('data-bind')) {
              element.dispatchEvent(
                new CustomEvent('change-bind', {
                  bubbles: true,
                  cancelable: true,
                  composed: true,
                })
              );
            }
          }
          break;
        case 'data-bind-fetch':
          this.doFetch(element);
          break;
        case 'data-bind-each':
          this.doEach(element);
          break;
        case 'data-bind-if':
          this.doIf(element);
          break;
        case 'data-bind-invalid':
          this.bind(element);
          break;
        case 'data-bind-transfer':
        case transfers != null && (<Array<string>>transfers)[0]:
          this.transfer(element);
          break;
        default:
          if (mutation.attributeName != null) {
            // Haori以外による変更はデータとして扱い、スクリプトとして実行しない
            this.unregisterAttribute(element, mutation.attributeName);
          }
      }
    });
  }

  /**
   * イベントの発生を抑制するエレメントを登録します。
   * イベントが派生するたびに登録し直す必要があります。
   *
   * @param element 対象エレメント
   */
  static setStopEventElements(element: Element) {
    this.stopEventElements.add(element);
  }

  /**
   * 対象の処理必要の有無を取得します。
   *
   * @param element 対象エレメント
   * @return 処理が不要な場合はtrue
   */
  private static isInvalid(element: Element | null) {
    if (element == null) {
      return true;
    }
    if (element.closest('html') == null) {
      // DOMに組み込まれていない場合
      return true;
    }
    if (Haori.hasClosestAttribute(element, 'data-bind-invalid')) {
      return true;
    }
    if (Haori.hasClosestAttribute(element, 'data-bind-if-false')) {
      return true;
    }
    return false;
  }

  /**
   * 対象エレメントを含む子孫エレメントのdata-bind-*およびスクリプト処理を行います。
   *
   * @param element 対象エレメント
   * @return 変更した場合はtrue
   */
  static bind(element: Element): boolean {
    let result = false;
    let currentValue = this.getValue(element);
    if (element.hasAttribute('data-bind-if')) {
      if (this.doIf(element)) {
        result = true;
      }
    }
    if (element.hasAttribute('data-bind-each')) {
      if (this.doEach(element)) {
        result = true;
      }
    }
    if (this.doScriptAttributes(element, true)) {
      result = true;
    }
    if (element.hasAttribute('data-bind-fetch')) {
      if (this.doFetch(element)) {
        result = true;
      }
    }
    if (element.hasAttribute('data-bind-transfer')) {
      if (this.transfer(element)) {
        result = true;
      }
    }
    if (this.doScriptAttributes(element, true)) {
      result = true;
    }
    if (this.doScriptContent(element, true)) {
      result = true;
    }
    for (let i = 0; i < element.children.length; i++) {
      let child = <HTMLElement>element.children[i];
      if (this.bind(child)) {
        result = true;
      }
    }
    if (result && (element.tagName == 'INPUT' || element.tagName == 'SELECT' || element.tagName == 'TEXTAREA')) {
      setTimeout(() => {
        // optionのselectedを反映するためにsetTimeoutで判定を遅らせる
        let newValue = this.getValue(element);
        if (currentValue!.indexOf('{{') < 0 && newValue!.indexOf('{{') < 0 && currentValue != newValue) {
          element.setAttribute('data-disabled-change-fetch', ''); // bindでの変更はdata-change-fetchの対象外とする
          element.dispatchEvent(
            new CustomEvent('change', {
              bubbles: true,
              cancelable: true,
              composed: true,
            })
          );
          element.removeAttribute('data-disabled-change-fetch');
        }
      });
    }
    return result;
  }

  /**
   * 対象エレメントのvalue値を取得します。エレメントが存在しない場合はnullを返します。
   *
   * @param element 対象エレメント
   * @return value値の値
   */
  static getValue(element: Element): string | null {
    if (element && element.tagName) {
      switch (element.tagName) {
        case 'INPUT':
          return (<HTMLInputElement>element).value;
        case 'TEXTAREA':
          return (<HTMLTextAreaElement>element).value;
        case 'SELECT':
          let value = (<HTMLSelectElement>element).value;
          if (value == '' && element.querySelector('option[value=""],option:not([value])') == null) {
            // 中身が変わっていてvalueが空文字列になっている場合はvalueを再設定する
            let option = element.querySelector('option[selected]');
            if (option == null) {
              option = element.querySelector('option');
            }
            if (option != null) {
              value = option.getAttribute('value')!;
              (<HTMLSelectElement>element).value = value;
            }
          }
          return value;
      }
    }
    return null;
  }

  /**
   * data-bind-fetchの値からデータを取得してdata-bindに設定します。
   *
   * @param element 処理対象のエレメント
   * @return 変更した場合はtrue
   */
  static doFetch(element: Element): boolean {
    if (this.isInvalid(element.parentElement)) {
      return false;
    }
    let url = element.getAttribute('data-bind-fetch');
    if (url == null) {
      console.error('data-bind-fetch属性が指定されていません', element);
      return false;
    }
    if (url.indexOf('{{') >= 0) {
      // プレースホルダが存在する場合はスキップする
      return false;
    }
    if (element.hasAttribute('data-bind-fetch-current')) {
      if (element.getAttribute('data-bind-fetch-current') == url) {
        // 変更なし
        return false;
      }
    }
    element.setAttribute('data-bind-fetch-current', url);
    let fetch = new HaoriFetch();
    fetch.setParams(element, 'data-bind');
    fetch.bindElements = [element];
    fetch.bindArg = element.getAttribute('data-bind-fetch-arg');
    fetch.bindMerge = element.getAttribute('data-bind-fetch-merge') ? true : null;
    if (element.hasAttribute('data-bind-fetch-redirect')) {
      fetch.redirectUrl = element.getAttribute('data-bind-fetch-redirect')!;
    }
    if (element.hasAttribute('data-bind-fetch-click')) {
      fetch.clickElements = [<HTMLElement>element];
    }
    if (element.tagName == 'FORM') {
      fetch.form = <HTMLFormElement>element;
    }
    fetch.fetch(url, element);
    return true;
  }

  /**
   * 繰り返しの前後に表示するノードだけの状態に戻します。
   *
   * @param element 対象エレメント
   */
  private static restoreBeforeEach(element: Element) {
    element.innerHTML = '';
    let nodes = this.srcBeforeEachNodes.get(element);
    if (nodes != null) {
      nodes.forEach((node) => {
        element.appendChild(HaoriBind.clone(node));
      });
    }
  }

  /**
   * data-bind-eachの値をリストとして子エレメントを複製します。
   *
   * @param element 処理対象のエレメント
   * @return 変更した場合はtrue
   */
  static doEach(element: Element): boolean {
    if (this.isInvalid(element.parentElement)) {
      let templates = this.srcEachs.get(element);
      if (templates != null) {
        this.restoreBeforeEach(element);
        templates.forEach((template: Element) => {
          element.appendChild(HaoriBind.clone(template));
        });
        return true;
      }
      return false;
    }
    let script = element.getAttribute('data-bind-each');
    if (script == null) {
      console.error('data-bind-each属性が指定されていません', element);
      return false;
    } else if (script.indexOf('{{') >= 0) {
      console.error('data-bind-eachに{{}}は不要です', element);
      return false;
    }
    let beforeHtml = this.srcBeforeEachs.get(element) || '';
    let templates = this.srcEachs.get(element);
    if (templates == null) {
      beforeHtml = '';
      templates = [];
      let beforeNodes = new Array<Node>();
      for (let i = 0; i < element.children.length; i++) {
        let child = element.children[i];
        if (child.hasAttribute('data-bind-each-before') || child.hasAttribute('data-bind-each-after')) {
          beforeHtml += child.outerHTML;
          beforeNodes.push(HaoriBind.clone(child));
        } else {
          templates.push(child);
        }
      }
      this.srcBeforeEachs.set(element, beforeHtml);
      this.srcBeforeEachNodes.set(element, beforeNodes);
      this.srcEachs.set(element, templates);
      this.restoreBeforeEach(element);
    }
    let params = this.getBindObject(element);
    if (params == null) {
      if (element.innerHTML != beforeHtml) {
        this.restoreBeforeEach(element);
        return true;
      }
      return false;
    }
    let list: any = null;
    try {
      list = Haori.executeScript(params, script, element);
    } catch (error) {
      if (element.innerHTML != beforeHtml) {
        this.restoreBeforeEach(element);
        return true;
      }
      return false;
    }
    if (list == null) {
      list = [];
    }
    if (!Array.isArray(list)) {
      console.error('data-bind-eachの実行結果がリストではありません', element, list);
      if (element.innerHTML != beforeHtml) {
        this.restoreBeforeEach(element);
        return true;
      }
      return false;
    }
    let bindString = this.eachBindStrings.get(element);
    let bindScript = this.eachScripts.get(element);
    let json = JSON.stringify(list);
    if (bindScript == script && bindString == json) {
      return false;
    }
    this.eachScripts.set(element, script);
    this.eachBindStrings.set(element, json);
    let newElements = new Array<Element>();
    list.forEach((object: any, index: number) => {
      if (typeof object == 'object') {
        object['index'] = index;
      }
      let listBind: any;
      if (element.hasAttribute('data-bind-each-arg')) {
        listBind = {};
        listBind[<string>element.getAttribute('data-bind-each-arg')] = object;
        if (typeof object != 'object') {
          listBind['index'] = index;
        }
      } else {
        listBind = object;
      }
      if (templates) {
        let json = JSON.stringify(listBind);
        for (let i = 0; i < templates.length; i++) {
          let template = <HTMLElement>HaoriBind.clone(templates[i]);
          template.setAttribute('data-bind', json);
          newElements.push(template);
        }
      }
    });
    // 差分のみ追加・削除する
    let j = 0;
    let beforeElementCount = 0;
    let afterElementCount = 0;
    for (let i = 0; i < element.children.length; i++) {
      let currentElement = <Element>element.children.item(i);
      if (currentElement.hasAttribute('data-bind-each-before')) {
        beforeElementCount++;
      }
      if (currentElement.hasAttribute('data-bind-each-after')) {
        afterElementCount++;
      }
    }
    let currentElements = new Array<Element>();
    for (let i = beforeElementCount; i < element.children.length - afterElementCount; i++) {
      currentElements.push(<Element>element.children.item(i));
    }
    for (let i = 0; i < currentElements.length; i++) {
      let currentElement = currentElements[i];
      for (; j < newElements.length; j++) {
        let newElement = newElements[j];
        if (currentElement.getAttribute('data-bind') == newElement.getAttribute('data-bind')) {
          j++;
          break;
        }
        Haori.insertBefore(newElement, currentElement);
      }
    }
    for (let i = element.children.length - 1 - afterElementCount; i > beforeElementCount + j - 1; i--) {
      element.children.item(i)?.remove();
    }
    for (; j < newElements.length; j++) {
      let after = element.querySelector('[data-bind-each-after]');
      if (after == null) {
        element.appendChild(newElements[j]);
      } else {
        Haori.insertBefore(newElements[j], after);
      }
    }
    return true;
  }

  /**
   * data-bind-ifの値をスクリプトとして戻り値によって表示・非表示を切り替えます。
   *
   * @param element 処理対象のエレメント
   * @return 変更した場合はtrue
   */
  static doIf(element: Element): boolean {
    if (this.isInvalid(element.parentElement)) {
      if (element.hasAttribute('data-bind-if-false')) {
        let nodes = this.srcIfElements.get(element);
        if (nodes != null) {
          nodes.forEach((node) => {
            element.appendChild(node);
          });
        }
        (<HTMLElement>element).style.display = '';
        element.removeAttribute('data-bind-if-false');
        return true;
      }
      return false;
    }
    let script = element.getAttribute('data-bind-if');
    if (script == null) {
      console.error('data-bind-if属性が指定されていません', element);
      return false;
    } else if (script.indexOf('{{') >= 0) {
      console.error('data-bind-ifに{{}}は不要です', element);
      return false;
    }
    let params = this.getBindObject(element);
    let result: any;
    if (params == null) {
      result = false;
    } else {
      try {
        result = Haori.executeScript(params, script, element);
      } catch (error) {
        result = false;
      }
    }
    if (result === false || result === null || result === undefined) {
      if (element.hasAttribute('data-bind-if-false')) {
        return false;
      }
      if (this.srcIfElements.get(element) == null) {
        let list = new Array<Node>();
        element.childNodes.forEach((node) => {
          list.push(node);
        });
        this.srcIfElements.set(element, list);
      }
      element.innerHTML = '';
      (<HTMLElement>element).style.display = 'none';
      if (element.tagName == 'INPUT' || element.tagName == 'SELECT' || element.tagName == 'TEXTAREA' || element.tagName == 'BUTTON') {
        // フォーム要素やボタンはdisabledにする
        if (!element.hasAttribute('disabled')) {
          element.setAttribute('disabled', '');
          element.setAttribute('data-bind-if-disabled', '');
        }
      }
      element.setAttribute('data-bind-if-false', '');
    } else {
      if (!element.hasAttribute('data-bind-if-false')) {
        return false;
      }
      let nodes = this.srcIfElements.get(element);
      if (nodes != null) {
        nodes.forEach((node) => {
          element.appendChild(node);
        });
      }
      (<HTMLElement>element).style.display = '';
      if (element.tagName == 'INPUT' || element.tagName == 'SELECT' || element.tagName == 'TEXTAREA' || element.tagName == 'BUTTON') {
        // フォーム要素やボタンはdisabledを解除する
        if (element.hasAttribute('data-bind-if-disabled')) {
          element.removeAttribute('disabled');
          element.removeAttribute('data-bind-if-disabled');
        }
      }
      if (element.hasAttribute('data-bind-if-false')) {
        element.removeAttribute('data-bind-if-false');
      }
    }
    return true;
  }

  /**
   * 対象エレメントの属性のスクリプトを処理します。
   *
   * @param element 処理対象のエレメント
   * @param useSrc 保存された元ソースを使う場合はtrue
   * @return 変更した場合はtrue
   */
  static doScriptAttributes(element: Element, useSrc: boolean): boolean {
    let result = false;
    element.getAttributeNames().forEach((attribute) => {
      if (this.doScriptAttribute(element, attribute, useSrc)) {
        result = true;
      }
    });
    this.removeAttributes.get(element)?.forEach((attribute) => {
      if (this.doScriptAttribute(element, attribute, useSrc)) {
        result = true;
      }
    });
    return result;
  }

  /**
   * 対象エレメントの属性のスクリプトを処理します。
   * data-bind、data-bind-if、data-bind-each属性の場合は何もしません。
   * テンプレートとして登録したアトリビュートだけを処理し、登録していないアトリビュートはデータとしてそのままにします。
   *
   * @param element 処理対象のエレメント
   * @param attribute 処理対象のアトリビュート
   * @param useSrc 互換のための引数（現在の値をテンプレートとして読み直すとデータをスクリプトとして実行してしまうため、常に登録した元ソースを使います）
   * @return 変更した場合はtrue
   */
  static doScriptAttribute(element: Element, attribute: string, useSrc: boolean): boolean {
    if (attribute == 'data-bind' || attribute == 'data-bind-if' || attribute == 'data-bind-each') {
      return false;
    }
    let text = this.srcAttributes.get(element)?.get(attribute);
    if (this.isInvalid(element.parentElement)) {
      if (text != null && element.getAttribute(attribute) != text) {
        this.addAttributeChange(element, attribute);
        element.setAttribute(attribute, text);
        return true;
      }
      return false;
    }
    if (text == null || text.indexOf('{{') < 0) {
      return false;
    }
    let params = this.getBindObject(element);
    let newText: any;
    if (params == null || this.isInvalid(element)) {
      newText = text;
    } else {
      newText = this.replaceScript(params, text, element);
      if (newText === true) {
        newText = '';
      }
    }
    let result = false;
    if (newText === false || newText === null || newText === undefined) {
      if (element.hasAttribute(attribute)) {
        this.addAttributeChange(element, attribute);
        let attrSet = this.removeAttributes.get(element);
        if (attrSet == null) {
          attrSet = new Set();
          this.removeAttributes.set(element, attrSet);
        }
        attrSet.add(attribute);
        element.removeAttribute(attribute);
        result = true;
      }
    } else {
      if (element.getAttribute(attribute) != newText) {
        let attrSet = this.removeAttributes.get(element);
        if (attrSet != null) {
          attrSet.delete(attribute);
        }
        this.addAttributeChange(element, attribute);
        element.setAttribute(attribute, newText);
        result = true;
      }
    }
    return result;
  }

  /**
   * 対象エレメントのテキストコンテンツ内のスクリプトを処理します。
   * テンプレートとして登録したテキストだけを処理し、登録していないテキストはデータとしてそのままにします。
   *
   * @param node 処理対象のエレメント
   * @param useSrc 互換のための引数（現在の値をテンプレートとして読み直すとデータをスクリプトとして実行してしまうため、常に登録した元ソースを使います）
   * @return 変更した場合はtrue
   */
  static doScriptContent(node: Node, useSrc: boolean): boolean {
    if (node.nodeType == Node.ELEMENT_NODE && (<Element>node).tagName == 'SCRIPT') {
      return false;
    }
    if (node.nodeType == Node.ELEMENT_NODE && node.childNodes.length == 1) {
      let element = <HTMLElement>node;
      let text = this.srcContents.get(element);
      if (text != null && text.indexOf('!{{') == 0 && text.indexOf('}}!') == text.length - 3) {
        // HTML として解釈する
        let params = this.getBindObject(<Element>element);
        if (params) {
          let newText = this.replaceScript(params, text, node);
          newText = newText.trim();
          newText = newText.substring(1, newText.length - 1);
          newText = DOMPurify.sanitize(newText);
          if (element.innerHTML == newText) {
            return false;
          } else {
            element.innerHTML = newText;
            // 実行結果のHTMLはデータのため、中の{{}}はスクリプトとして実行しない
            element.childNodes.forEach((child) => {
              this.markAsData(child);
            });
            return true;
          }
        }
      }
    }
    let result = false;
    let params = null;
    for (let i = 0; i < node.childNodes.length; i++) {
      let child = node.childNodes[i];
      if (child.nodeType != Node.TEXT_NODE) {
        continue;
      }
      let text = <string | null>this.srcContents.get(child);
      if (node.nodeType == Node.TEXT_NODE && this.isInvalid(<Element>node.parentElement)) {
        if (text != null && node.textContent != text) {
          this.addContentChange(node);
          node.textContent = text;
          return true;
        }
        return false;
      }
      if (text == null || text.indexOf('{{') < 0) {
        continue;
      }
      if (params == null) {
        let element: any = node;
        if (node.nodeType != Node.ELEMENT_NODE) {
          element = node.parentElement;
        }
        if (element != null) {
          params = this.getBindObject(<Element>element);
        }
      }
      let newText: any;
      if (params == null) {
        newText = text;
      } else {
        newText = this.replaceScript(params, text, node);
        if (newText === false) {
          newText = text;
        }
      }
      if (newText !== null && typeof newText === 'object') {
        console.log('オブジェクトがテキストとして設定されました', child, newText);
      }
      if (child.textContent != newText) {
        this.addContentChange(child);
        child.textContent = newText;
        result = true;
      }
    }
    return result;
  }

  /**
   * テキスト文字列内のスクリプトを実行して戻り値と置換します。
   *
   * @param params 変数として設定するパラメータ
   * @param text テキスト文字列
   * @param element 対象エレメント（エラー時のログ用）
   * @return 置換したテキスト文字列（スクリプトがひとつの場合はその戻り値）
   */
  static replaceScript(params: any, text: string, element: Node | null = null): any {
    let newText: any = null;
    let lastEnd = 0;
    while (true) {
      let start = text.indexOf('{{', lastEnd);
      if (start < 0) {
        if (lastEnd == 0) {
          newText = text;
        } else if (lastEnd >= text.length) {
          break;
        } else {
          if (newText == null) {
            newText = '';
          }
          newText += text.substring(lastEnd);
        }
        break;
      }
      let end = text.indexOf('}}', start);
      if (end < 0) {
        if (lastEnd == 0) {
          newText = text;
        } else {
          if (newText == null) {
            newText = '';
          }
          newText += text.substring(lastEnd);
        }
        break;
      }
      if (lastEnd < start) {
        if (newText == null) {
          newText = '';
        }
        newText += text.substring(lastEnd, start);
      }
      let script = text.substring(start + 2, end);
      let result: any;
      try {
        result = Haori.executeScript(params, script, element);
      } catch (error) {
        return text;
      }
      if (result == null) {
        return false;
      }
      if (newText == null) {
        newText = result;
      } else {
        newText += result;
      }
      lastEnd = end + 2;
    }
    return newText;
  }

  /**
   * HTMLの解析中に読み込まれたかを取得します。
   *
   * @return HTMLの解析中に読み込まれた場合はtrue
   */
  static isLoadedWhileParsing(): boolean {
    return this.loadedWhileParsing;
  }

  /**
   * 対象エレメントおよびその先祖エレメントに割り当てられたオブジェクトを取得します。
   * パラメータ名が同じ場合は子エレメントのものが優先されます。
   *
   * @param element 対象エレメント
   * @return エレメントに割り当てられたオブジェクト
   */
  static getBindObject(element: Element): any {
    if (this.isInvalid(element.parentElement)) {
      return null;
    }

    let object: any | null = null;
    let bindString = (<Element>element).getAttribute('data-bind');
    if (bindString != null && bindString.length > 0 && (bindString.startsWith('[') || bindString.startsWith('{'))) {
      object = JSON.parse(bindString);
    }
    if ((<Element>element).hasAttribute('data-bind-default')) {
      let defaultString = (<Element>element).getAttribute('data-bind-default');
      if (defaultString != null && defaultString.length > 0) {
        let defaultObject = JSON.parse(defaultString);
        object = { ...defaultObject, ...object };
      }
    }

    let parent = element.parentElement;
    if (parent == null || parent.tagName == 'HTML') {
      return object || {};
    }
    let parentObject = this.getBindObject(parent);
    if (object == null && parentObject == null) {
      return null;
    }
    return { ...parentObject, ...object };
  }

  /**
   * 属性の値を変更します。変更元の値にスクリプトが含まれている場合は属性を付け替えません。
   *
   * @param element 対象エレメント
   * @return 変更した場合はtrue
   */
  static transfer(element: Element): boolean {
    if (this.isInvalid(element.parentElement)) {
      return false;
    }
    let transfers: Array<string> | null = null;
    if (element.hasAttribute('data-bind-transfer')) {
      transfers = (<string>element.getAttribute('data-bind-transfer')).split(' ');
    }
    if (transfers == null || transfers.length != 2) {
      transfers = null;
      console.error('data-bind-transfer属性の値は（変換元属性名）" "（変換先属性名）で記述してください');
      return false;
    }
    if (!element.hasAttribute(transfers[0])) {
      return false;
    }
    let result = false;
    let value = element.getAttribute(transfers[0]);
    if (value == null) {
      if (element.hasAttribute(transfers[1])) {
        result = true;
      }
    } else {
      if (value.indexOf('{{') >= 0) {
        return false;
      }
      setTimeout(() => {
        element.setAttribute(transfers[1], value);
      });
      result = true;
    }
    if (result && element.tagName == 'SOURCE') {
      let video = <HTMLVideoElement>element.closest('video');
      if (video) {
        video.load();
      }
    }
    return result;
  }
}

// 読み込み済みのHTMLはテンプレートとして登録する（以後に解析されるHTMLはchildObserverとDOMContentLoadedで登録する）
// HTMLの解析が終わってから読み込まれた場合（defer属性など）は、この時点で全体を登録できるため、DOMContentLoadedでは登録しない
HaoriBind.registerTemplate(document.documentElement);

HaoriBind.attributeObserver.observe(document, {
  attributes: true,
  subtree: true,
  attributeOldValue: true,
});

HaoriBind.contentObserver.observe(document, {
  characterData: true,
  subtree: true,
});

HaoriBind.childObserver.observe(document, {
  childList: true,
  subtree: true,
});

document.addEventListener('DOMContentLoaded', () => {
  // HTMLの解析で追加されたエレメントのうち、まだ登録していないものをテンプレートとして登録する
  // （データとして挿入したノードと登録済みのテキスト・アトリビュートは変更しない）
  if (HaoriBind.isLoadedWhileParsing()) {
    HaoriBind.registerTemplate(document.documentElement);
  }
  HaoriBind.bind(document.head);
  HaoriBind.bind(document.body);
});

window.addEventListener('pageshow', (event) => {
  let entries = performance.getEntriesByType('navigation');
  if (entries.length == 0) {
    return;
  }
  if (event.persisted) {
    // キャッシュから表示された場合
    document.querySelectorAll('[data-bind-fetch-current]').forEach((element) => {
      element.removeAttribute('data-bind-fetch-current');
    });
    HaoriBind.bind(document.head);
    HaoriBind.bind(document.body);
  }
});
